import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

const FREE_DEFAULTS = {
  tier: { id: "free", name: "Free", price_aud: 0, job_limit: 5, monthly_credits: 0, lead_marketplace: true },
  subscription: null,
  jobCompletions: 0,
  loading: false,
  isTrialing: false,
  isTrialExpired: false,
  isFree: true,
  isSoleTrader: false,
  isProfessional: false,
  isEnterprise: false,
  isPaid: false,
  canAccessLeads: true,
  isJobLimitReached: false,
  trialDaysRemaining: 0,
  monthlyCreditsAllowance: 0,
};

const LOADING_DEFAULTS = {
  ...FREE_DEFAULTS,
  tier: null,
  loading: true,
  isFree: false,
  isSoleTrader: false,
  isProfessional: false,
  isEnterprise: false,
  isPaid: false,
};

export function getSubscriptionLoadingState() {
  return { ...LOADING_DEFAULTS };
}

// Tier IDs valid for simulation. "trial" is a special pseudo-tier (status-based, not a DB tier).
const SIM_TIER_IDS = ["free", "trial", "sole_trader", "professional", "enterprise"];

export function useSubscription({ businessId }) {
  const [state, setState] = useState(getSubscriptionLoadingState());
  // Simulation is session-only React state — never written to the database.
  // Security: simulation is purely cosmetic/local; all DB access still uses the real tier.
  const [simulatedTierId, setSimulatedTier] = useState(null);

  useEffect(() => {
    if (!businessId) {
      setState(getSubscriptionLoadingState());
      return;
    }

    let cancelled = false;

    async function load() {
      setState((prev) => ({ ...prev, loading: true }));

      // 1. Fetch subscription row joined with tier
      let { data: sub, error: subError } = await supabase
        .from("subscriptions")
        .select("*, tier:subscription_tiers(*)")
        .eq("business_id", businessId)
        .maybeSingle();

      if (cancelled) return;

      if (subError) {
        // Fail silently — hook falls back to FREE_DEFAULTS below.
      }

      // 2. If no row, insert a free-tier subscription and use defaults
      if (!sub) {
        // Upsert avoids a race condition when two tabs load simultaneously — a plain INSERT
        // would fail with a unique-constraint error on the second concurrent call.
        const { data: inserted } = await supabase
          .from("subscriptions")
          .upsert({ business_id: businessId, tier_id: "free", status: "active" }, { onConflict: "business_id", ignoreDuplicates: true })
          .select("*, tier:subscription_tiers(*)")
          .maybeSingle();
        if (!cancelled) sub = inserted;
      }

      if (cancelled) return;

      // 3. Count completed jobs within the current billing period only.
      // Use a plain SELECT + array length instead of `count: "exact",
      // head: true` -- the HEAD-with-exact-count form returns 503 from
      // PostgREST on this specific table (the underlying SQL+RLS work
      // when verified via psql, so the 503 is a gateway-layer quirk).
      // The result set is bounded by one tradie's completions in the
      // current billing period, so retrieving the rows is cheap.
      const periodStart = sub?.current_period_start ?? sub?.created_at ?? null;
      const completionsQuery = supabase
        .from("job_completions")
        .select("id")
        .eq("business_id", businessId);
      const { data: completionRows, error: completionsError } = await (periodStart
        ? completionsQuery.gte("completed_at", periodStart)
        : completionsQuery);
      const jobCompletions = completionsError ? 0 : (completionRows?.length || 0);

      if (cancelled) return;

      const tier = sub?.tier || FREE_DEFAULTS.tier;
      const subscription = sub
        ? { status: sub.status, trial_ends_at: sub.trial_ends_at, current_period_end: sub.current_period_end }
        : null;

      const completions = jobCompletions || 0;
      const tierId = tier.id || "free";
      const now = new Date();

      const isTrialing =
        sub?.status === "trialing" &&
        sub?.trial_ends_at != null &&
        new Date(sub.trial_ends_at) > now;

      const isTrialExpired =
        sub?.status === "trialing" &&
        sub?.trial_ends_at != null &&
        new Date(sub.trial_ends_at) <= now;

      const trialDaysRemaining = isTrialing
        ? Math.max(0, Math.ceil((new Date(sub.trial_ends_at) - now) / (1000 * 60 * 60 * 24)))
        : 0;

      const isFree = tierId === "free";
      const isSoleTrader = tierId === "sole_trader";
      const isProfessional = tierId === "professional";
      const isEnterprise = tierId === "enterprise";
      const isPaid = isSoleTrader || isProfessional || isEnterprise;

      const canAccessLeads = true; // all tiers can access the lead marketplace

      const isJobLimitReached =
        tier.job_limit != null && completions >= tier.job_limit;

      setState({
        tier,
        subscription,
        jobCompletions: completions,
        loading: false,
        isTrialing,
        isTrialExpired,
        isFree,
        isSoleTrader,
        isProfessional,
        isEnterprise,
        isPaid,
        canAccessLeads,
        isJobLimitReached,
        trialDaysRemaining,
        monthlyCreditsAllowance: tier.monthly_credits || 0,
      });
    }

    load();
    return () => { cancelled = true; };
  }, [businessId]);

  // ── Simulation overlay ──────────────────────────────────────────────────────
  // When simulatedTierId is active, all tier-derived booleans reflect the simulated
  // tier instead of the real one. The raw state (real subscription data) is unchanged.
  const validSim = simulatedTierId && SIM_TIER_IDS.includes(simulatedTierId)
    ? simulatedTierId
    : null;

  let returnValue = {
    ...state,
    simulatedTierId: validSim,
    setSimulatedTier: (id) => setSimulatedTier(id || null),
  };

  if (validSim) {
    const simIsTrialing = validSim === "trial";
    const effectiveTierId = simIsTrialing ? "free" : validSim;

    returnValue = {
      ...returnValue,
      isTrialing: simIsTrialing,
      isTrialExpired: false, // simulation never shows expired state
      trialDaysRemaining: simIsTrialing ? 7 : 0,
      isFree: effectiveTierId === "free",
      isSoleTrader: effectiveTierId === "sole_trader",
      isProfessional: effectiveTierId === "professional",
      isEnterprise: effectiveTierId === "enterprise",
      isPaid: ["sole_trader", "professional", "enterprise"].includes(effectiveTierId),
      canAccessLeads: true,
      isJobLimitReached: effectiveTierId === "free" ? state.isJobLimitReached : false,
    };
  }

  return returnValue;
}

/**
 * Returns a plain-English upgrade prompt string, or null if none is needed.
 * Pass the full return value of useSubscription().
 */
export function getUpgradeReason(hook) {
  if (!hook || hook.loading) return null;

  if (hook.isTrialing && hook.trialDaysRemaining <= 3) {
    const d = hook.trialDaysRemaining;
    return `Your trial ends in ${d} ${d === 1 ? "day" : "days"}. Upgrade to keep access.`;
  }

  if (hook.isJobLimitReached && hook.isFree) {
    return "You've used your 5 free jobs this month. Upgrade to Pro for 20 jobs/mo.";
  }

  if (hook.isJobLimitReached && hook.isSoleTrader) {
    return "You've reached your 20 job limit this month. Upgrade to Ultimate for unlimited.";
  }

  return null;
}
